import type { FieldDefinition } from '@src/utils/fieldDefinitionUtils';

export const floatingRateLoanFieldDefinitions = {
  counterparty: {
    title: 'Counterparty',
    tooltip:
      'The counterparty to the contract. This list contains those associated with a Credit Support Annex (CSA) and at least one internal Market Account.',
    columnTooltip: 'The counterparty to the contract',
  },
  marketAccount: {
    title: 'Market Account',
    tooltip: 'The Lending Market Account for the counterparty',
  },
  rate: {
    title: 'Rate',
    tooltip: '(Current calculated) annual interest rate',
  },
  creditSupportAnnex: {
    title: 'Credit Support Annex',
    tooltip: 'The credit support annex to the contract. This list contains those associated with the counterparty.',
    columnTooltip: 'The credit support annex to the contract',
  },
  tradeDate: {
    title: 'Origination Date',
    tooltip: 'Initial booking date for the loan',
  },
  effectiveDate: {
    title: 'Effective Date',
    tooltip: 'Date on which the loan becomes active',
  },
  term: {
    title: 'Term',
    tooltip: 'The duration of the loan (e.g. 1M, 1Y)',
  },
  terminationDate: {
    title: 'Termination Date',
    tooltip: 'In case of early termination or prepayment, the date on which the loan ends before Maturity Date',
  },
  maturityDate: {
    title: 'Maturity Date',
    tooltip: 'Loan maturity date, with corrections for date adjustments',
  },
  loanSide: {
    title: 'Side',
    tooltip:
      'Side of the loan for the booking party, e.g. for an originator this is generally always LEND unless booking a loan for borrowing risk capital',
  },
  unitLoanAmount: {
    title: 'Unit Loan Amount',
    tooltip:
      'Base loan size. When pricing a loan, Talos assumes this unit size for borrow/lend amount. You can then create loans of any amount from it.',
  },
  loanAmount: {
    title: 'Loan Amount',
    tooltip: 'Total amount of the borrowed asset to borrow/lend',
  },
  rateSource: {
    title: 'Rate Source',
    tooltip: 'Fixing rate source for the floating rate',
  },
  resetPeriod: {
    title: 'Rate Reset Period',
    tooltip: 'Frequency at which the floating rate gets reset',
  },
  initialCollateralPercent: {
    title: 'Initial Collateral %',
    tooltip:
      'Override to the default Initial Collateral % with this counterparty in case the lender offers a break or requires more collateral for this particular deal',
  },
  initialCollateralAmount: {
    title: 'Initial Collateral Amount',
    tooltip:
      'Based on borrowed amount, collateral price source rate and initial collateral % in force, the amount of collateral that must be posted',
  },
  pledgedCollateral: {
    title: 'Pledged Collateral',
    tooltip: 'Amount of collateral asset pledged for this loan',
  },
  paymentPeriod: {
    title: 'Payment Period',
    tooltip: 'How frequently coupons are paid on the loan',
  },
  loanPaymentType: {
    title: 'Payment Type',
    tooltip: 'Whether coupons are paid periodically or in a single payment at maturity',
  },
  accrualPeriod: {
    title: 'Accrual Period',
    tooltip: 'Length of time over which interest accrues; by default the same as Payment Period',
  },
  dayCountConvention: {
    title: 'Day Count Convention',
    tooltip:
      'Convention used to compute the number of days in an accrual period, e.g. 30/360 assumes each month has 30 days',
  },
  loanOriginationFee: {
    title: 'Loan Origination Fee',
    tooltip: 'Origination fee for the loan, as bps on the borrowed amount, if any',
  },
  loanPaymentCcy: {
    title: 'Loan Payment Ccy',
    tooltip: 'Currency accepted for payment of the commission and interest payments, if any',
  },
  termType: {
    title: 'Term Type',
    tooltip:
      'Whether the loan has a defined term up front (Fixed) or if, after an initial fixed term, the term becomes open-ended (Open).',
  },
  initialFixedTerm: {
    title: 'Initial Fixed Term',
    tooltip: "Interval for the loan's fixed term, e.g. 3M. Only applies if Term Type is Open.",
  },
  prepaymentOption: {
    title: 'Prepayment Option',
    tooltip:
      'Whether the borrower has a pre-payment option on the loan. This only comes into effect after the Initial Fixed Term.',
  },
  callOption: {
    title: 'Call Option',
    tooltip:
      'Whether the lender has a call option on the loan. This only comes into effect after the Initial Fixed Term.',
  },
  quantity: {
    title: 'Quantity',
    tooltip: 'Number of units of loan, in Unit Loan Amount, or absolute amount of borrowed asset',
  },
  settleTime: {
    title: 'Settlement Date',
    tooltip: 'Settlement date, with corrections for date adjustments',
  },
  settlementCycle: {
    title: 'Settlement Cycle',
    tooltip:
      'Window to post collateral, deliver borrowed assets, return principal at end of contract, as well as the default window to settle any margin calls',
  },
  businessDayConvention: {
    title: 'Business Day Convention',
    tooltip: 'Roll convention to use if a contract date falls on a holiday',
  },
  holidayCalendars: {
    title: 'Holiday Calendars',
    tooltip: 'List of business centers (calendars), e.g. NYS, to apply when adjusting dates to business days',
  },
  borrowedAsset: {
    title: 'Borrowed Asset',
    tooltip: 'The asset being loaned to the borrower, e.g. USD',
  },
  collateralization: {
    title: 'Collateralization',
    tooltip: 'Whether the loan is secured by collateral (Secured) or has no collateral leg (Unsecured)',
    columnTooltip: 'Secured vs Unsecured',
  },
  collateralAsset: {
    title: 'Collateral Asset',
    tooltip:
      'The asset being held as collateral, e.g. BTC. Only assets in Lending market accounts (Admin -> Counterparties) that have fixings are available. Only applicable when Collateralization is Secured.',
  },
  productDetails: {
    title: 'Loan Fields',
    tooltip: 'Economic details for the loan being created',
  },
  leg: {
    title: 'Leg',
    tooltip: 'Order for the legs in a multi-leg instrument',
  },
  productTemplate: {
    title: 'Product Template',
    tooltip: 'Product template descriptive symbol',
  },
  suffix: {
    title: 'Suffix',
    tooltip: 'Optional tag to append to symbol, differentiating different product templates',
  },
  spread: {
    title: 'Spread (bps)',
    tooltip: 'For spread-to-fixing type deals, the spread over the Index',
  },
  // LTV threshold fields (thresholdsMinimum, thresholdsMarginCall, thresholdsLiquidation,
  // thresholdsCollateralRelease) and their Collateralization % counterparts are specified
  // in the MLA per the PRD. See mlaAgreementFieldDefinitions.ts for these field definitions.
} as const satisfies Record<string, FieldDefinition>;

export type FloatingRateLoanFieldKey = keyof typeof floatingRateLoanFieldDefinitions;
